// src/pages/PhotoManagement.tsx
import React, { useEffect, useState, useRef } from 'react';
import {
   collection,
   getDocs,
   doc,
   deleteDoc,
   updateDoc,
   query,
   where,
   orderBy,
   getDoc,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { format } from 'date-fns';
import {
   Box,
   Paper,
   Typography,
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   TablePagination,
   Chip,
   IconButton,
   Button,
   TextField,
   InputAdornment,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogContentText,
   DialogTitle,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   ListItem,
   SelectChangeEvent,
   Divider,
   FormControlLabel,
   Checkbox,
   useMediaQuery,
   useTheme,
   Container,
   Grid,
   Card,
   CardContent,
   CardMedia,
   CardActions,
} from '@mui/material';
import {
   Edit as EditIcon,
   Delete as DeleteIcon,
   LocationOn as LocationIcon,
   LocationOff as LocationOffIcon,
   Search as SearchIcon,
   Tag as TagIcon,
   Image as ImageIcon,
   FilterList as FilterIcon,
} from '@mui/icons-material';
import FixedListItemText from '../components/common/FixedListItemText';
import EditPhotoDetailsDialog from '../components/Photos/EditPhotoDetailsDialog';
import { useUser } from '../contexts/UserContext';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { get_user_transformed_photos } from '../services';
import PhotoLikeButton from '../components/common/PhotoLikeButton';
import { useResponsive, useResponsiveTypography, useResponsiveSpacing } from '../utils/responsive';

// Photo interface
interface Photo {
   id: string;
   filename: string;
   url: string;
   thumbnailUrl: string;
   title: string;
   description: string;
   takenAt: Date;
   uploadedAt: Date;
   location?: {
      name?: string;
      coordinates?: [number, number]; // [longitude, latitude]
   };
   tags: string[];
   tripId?: string;
   likeCount?: number;
   isLiked?: boolean;
}

// Trip interface
interface Trip {
   id: string;
   title: string;
   description?: string;
   startDate: Date;
   endDate: Date;
   coverPhotoId?: string;
   coverPhotoUrl?: string;
   locations?: string[]; // Optional list of location names
   tags?: string[];
   primaryDestination?: {
      coordinates?: [number, number];
   };
}

const PhotoManagement: React.FC = () => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();
   
   const [photos, setPhotos] = useState<Photo[]>([]);
   const [filteredPhotos, setFilteredPhotos] = useState<Photo[]>([]);
   const [loading, setLoading] = useState<boolean>(true);
   const [page, setPage] = useState(0);
   const [rowsPerPage, setRowsPerPage] = useState(10);
   const [searchTerm, setSearchTerm] = useState('');
   const [locationFilter, setLocationFilter] = useState<'all' | 'with' | 'without'>('all');
   const [tripFilter, setTripFilter] = useState<string>('all');
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
   const [locationDialogOpen, setLocationDialogOpen] = useState(false);
   const [photoToEditLocation, setPhotoToEditLocation] = useState<Photo | null>(null);
   const locationMapContainer = useRef<HTMLDivElement>(null);
   const locationMap = useRef<mapboxgl.Map | null>(null);
   const locationMarker = useRef<mapboxgl.Marker | null>(null);
   const [newCoordinates, setNewCoordinates] = useState<[number, number] | null>(null);
   const [trips, setTrips] = useState<Trip[]>([]);
   const [assignTripDialogOpen, setAssignTripDialogOpen] = useState(false);
   const [photoToAssignTrip, setPhotoToAssignTrip] = useState<Photo | null>(null);
   const [selectedTripId, setSelectedTripId] = useState<string>('');
   const [applyTripCoordinates, setApplyTripCoordinates] = useState<boolean>(false);
   const { currentUser } = useUser();

   // Add state for snackbar
   const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'info' | 'warning';
   }>({
      open: false,
      message: '',
      severity: 'info',
   });

   // State for edit photo details dialog
   const [editDetailsDialogOpen, setEditDetailsDialogOpen] = useState(false);
   const [photoToEdit, setPhotoToEdit] = useState<Photo | null>(null);

   const handleCloseSnackbar = () => {
      setSnackbar({ ...snackbar, open: false });
   };

   const fetchPhotos = async () => {
      try {
         setLoading(true);

         // First check if user is authenticated
         if (!currentUser || !currentUser.uid) {
            setPhotos([]);
            setFilteredPhotos([]);
            setLoading(false);
            return;
         }

         // Query all photos uploaded by the current user
         // We don't filter by tripId so we get ALL photos
         const transformedPhotos = (
            (await get_user_transformed_photos(currentUser.uid || '')) || []
         ).map((photo: any) => ({
            ...photo,
            id: photo.id,
            filename: photo.fileName || 'unknown',
            url: photo.url,
            thumbnailUrl: photo.thumbnailUrl || photo.url,
            title: photo.title || 'Untitled',
            description: photo.description || '',
            uploadedAt: photo.uploadedAt,
         }));

         console.log('transformedPhotos', transformedPhotos);

         const photosQuery = query(
            collection(db, 'photos'),
            where('uploadedBy', '==', currentUser.uid),
            orderBy('uploadedAt', 'desc')
         );

         const photoSnapshot = await getDocs(photosQuery);
         const photoData: Photo[] = [];

         photoSnapshot.forEach((doc) => {
            const docData = doc.data();

            // Convert Firestore timestamps to Date objects
            const takenAt = docData.takenAt?.toDate
               ? docData.takenAt.toDate()
               : docData.takenAt
                 ? new Date(docData.takenAt)
                 : new Date();

            const uploadedAt = docData.uploadedAt?.toDate
               ? docData.uploadedAt.toDate()
               : docData.uploadedAt
                 ? new Date(docData.uploadedAt)
                 : new Date();

            // Build the photo object
            const photo: Photo = {
               id: doc.id,
               filename: docData.fileName || 'unknown',
               url: docData.url,
               thumbnailUrl: docData.thumbnailUrl || docData.url,
               title: docData.title || 'Untitled',
               description: docData.description || '',
               takenAt,
               uploadedAt,
               location: docData.location,
               tags: docData.tags || [],
               tripId: docData.tripId,
            };

            photoData.push(photo);
         });

         setPhotos([...photoData, ...transformedPhotos]);
         setFilteredPhotos([...photoData, ...transformedPhotos]);
      } catch (error) {
         console.error('Error fetching photos:', error);
         // If this is an index error, show a more helpful message
         if (error instanceof Error && error.message.includes('index')) {
            console.error(
               'This query requires a Firestore index. Please follow the link in the console error to create it.'
            );
         }
      } finally {
         setLoading(false);
      }
   };

   // Fetch all trips the user has access to (owner, editor, viewer)
   const fetchTrips = async () => {
      try {
         if (!currentUser) return;

         // Use backend API to fetch trips where user is owner, editor, or viewer
         const { createApiUrl } = await import('../utils/apiConfig');
         const apiUrl = createApiUrl(
            `/api/trips/user/${currentUser.uid}?userEmail=${encodeURIComponent(currentUser.email || '')}`
         );

         const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
               'Content-Type': 'application/json',
            },
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to fetch trips');
         }

         // Transform the data to match the Trip interface
         const tripsData = (result.data || []).map((tripData: any) => {
            // Convert date strings back to Date objects
            const startDate = tripData.startDate ? new Date(tripData.startDate) : new Date();
            const endDate = tripData.endDate ? new Date(tripData.endDate) : new Date();

            const trip: Trip = {
               id: tripData.id,
               title: tripData.title || 'Unnamed Trip',
               description: tripData.description,
               startDate,
               endDate,
               coverPhotoId: tripData.coverPhotoId,
               coverPhotoUrl: tripData.coverPhotoUrl || tripData.coverImageUrl,
               locations: tripData.locations || [],
               tags: tripData.tags || [],
               primaryDestination: tripData.primaryDestination,
            };

            return trip;
         });

         // Sort by start date, newest first
         tripsData.sort((a: Trip, b: Trip) => b.startDate.getTime() - a.startDate.getTime());

         setTrips(tripsData);
      } catch (error) {
         console.error('Error fetching trips:', error);
      }
   };

   // Load photos and trips when component mounts
   useEffect(() => {
      fetchPhotos();
      fetchTrips();
   }, []);

   // Filter photos when search term, location filter, or trip filter changes
   useEffect(() => {
      let results = [...photos];

      // Apply search filter
      if (searchTerm) {
         const term = searchTerm.toLowerCase();
         results = results.filter(
            (photo) =>
               photo.title.toLowerCase().includes(term) ||
               photo.description?.toLowerCase().includes(term) ||
               photo.tags.some((tag) => tag.toLowerCase().includes(term))
         );
      }

      // Apply location filter
      if (locationFilter === 'with') {
         results = results.filter((photo) => photo.location && photo.location.coordinates);
      } else if (locationFilter === 'without') {
         results = results.filter((photo) => !photo.location || !photo.location.coordinates);
      }

      // Apply trip filter
      if (tripFilter !== 'all') {
         if (tripFilter === 'unassigned') {
            results = results.filter((photo) => !photo.tripId);
         } else {
            results = results.filter((photo) => photo.tripId === tripFilter);
         }
      }

      setFilteredPhotos(results);
      setPage(0); // Reset to first page when filters change
   }, [searchTerm, locationFilter, tripFilter, photos]);

   const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
   };

   const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
   };

   const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
   };

   const toggleLocationFilter = () => {
      if (locationFilter === 'all') setLocationFilter('with');
      else if (locationFilter === 'with') setLocationFilter('without');
      else setLocationFilter('all');
   };

   const handleTripFilterChange = (event: SelectChangeEvent) => {
      setTripFilter(event.target.value);
   };

   const handleDeleteClick = (photo: Photo) => {
      setPhotoToDelete(photo);
      setDeleteDialogOpen(true);
   };

   const handleDeleteCancel = () => {
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
   };

   const handleDeleteConfirm = async () => {
      if (!photoToDelete) return;

      setLoading(true);
      try {
         // Delete from Firestore
         await deleteDoc(doc(db, 'photos', photoToDelete.id));

         // Delete from Storage if we have a filename
         if (photoToDelete.filename) {
            try {
               const storageRef = ref(storage, `photos/${photoToDelete.filename}`);
               await deleteObject(storageRef);
            } catch (storageError) {
               console.error('Error deleting from storage:', storageError);
               // Continue even if storage delete fails
            }
         }

         // Remove from local state
         setPhotos(photos.filter((p) => p.id !== photoToDelete.id));
         setFilteredPhotos(filteredPhotos.filter((p) => p.id !== photoToDelete.id));
      } catch (error) {
         console.error('Error deleting photo:', error);
      } finally {
         setLoading(false);
         setDeleteDialogOpen(false);
         setPhotoToDelete(null);
      }
   };

   const handleEditLocation = (photo: Photo) => {
      setPhotoToEditLocation(photo);
      setNewCoordinates(photo.location?.coordinates || null);
      setLocationDialogOpen(true);
   };

   const handleLocationCancel = () => {
      setLocationDialogOpen(false);
      setPhotoToEditLocation(null);
      setNewCoordinates(null);
      if (locationMarker.current) {
         locationMarker.current.remove();
         locationMarker.current = null;
      }
      locationMap.current = null;
   };

   const handleLocationSave = async () => {
      if (!photoToEditLocation || !newCoordinates) return;

      setLoading(true);
      try {
         // Update in Firestore
         await updateDoc(doc(db, 'photos', photoToEditLocation.id), {
            location: {
               coordinates: newCoordinates,
            },
         });

         // Update in local state
         const updatedPhoto = {
            ...photoToEditLocation,
            location: {
               coordinates: newCoordinates,
            },
         };

         setPhotos(photos.map((p) => (p.id === photoToEditLocation.id ? updatedPhoto : p)));
         setFilteredPhotos(
            filteredPhotos.map((p) => (p.id === photoToEditLocation.id ? updatedPhoto : p))
         );

         // Close dialog
         handleLocationCancel();
      } catch (error) {
         console.error('Error updating location:', error);
      } finally {
         setLoading(false);
      }
   };

   // Initialize the map when the location dialog opens
   useEffect(() => {
      if (!locationDialogOpen || !locationMapContainer.current) return;

      // Use the hardcoded token that's working in other parts of the app
      mapboxgl.accessToken =
         'pk.eyJ1IjoiMjA0NXB1Ymxpc2hpbmciLCJhIjoiY203bDdtZnltMDhwMDJub2Nsenc3ZW9neiJ9.BYHR9d2yYc1Yg9mPSdisVg';

      const timer = setTimeout(() => {
         try {
            if (locationMap.current) locationMap.current.remove();

            locationMap.current = new mapboxgl.Map({
               container: locationMapContainer.current!,
               style: 'mapbox://styles/mapbox/streets-v11',
               center: newCoordinates || [0, 0],
               zoom: newCoordinates ? 10 : 1,
            });

            locationMap.current.on('load', () => {
               if (!locationMap.current) return;

               locationMap.current.addControl(new mapboxgl.NavigationControl());

               if (newCoordinates) {
                  locationMarker.current = new mapboxgl.Marker({ draggable: true })
                     .setLngLat(newCoordinates)
                     .addTo(locationMap.current);

                  locationMarker.current.on('dragend', () => {
                     const lngLat = locationMarker.current?.getLngLat();
                     if (lngLat) setNewCoordinates([lngLat.lng, lngLat.lat]);
                  });
               }

               locationMap.current.on('click', (e) => {
                  if (locationMarker.current) {
                     locationMarker.current.setLngLat(e.lngLat);
                  } else {
                     locationMarker.current = new mapboxgl.Marker({ draggable: true })
                        .setLngLat(e.lngLat)
                        .addTo(locationMap.current!);

                     locationMarker.current.on('dragend', () => {
                        const lngLat = locationMarker.current?.getLngLat();
                        if (lngLat) setNewCoordinates([lngLat.lng, lngLat.lat]);
                     });
                  }

                  setNewCoordinates([e.lngLat.lng, e.lngLat.lat]);
               });
            });
         } catch (error) {
            console.error('Error creating map:', error);
            // Display an error message in the map container
            if (locationMapContainer.current) {
               locationMapContainer.current.innerHTML = `
                  <div style="display:flex; justify-content:center; align-items:center; height:100%; flex-direction:column;">
                     <p>Error loading map. Please try again.</p>
                     <p style="font-size: 0.8rem; color: #666;">${error}</p>
                  </div>
               `;
            }
         }
      }, 300);

      return () => {
         clearTimeout(timer);
         if (locationMap.current) locationMap.current.remove();
      };
   }, [locationDialogOpen, newCoordinates]);

   const handleAssignTrip = (photo: Photo) => {
      setPhotoToAssignTrip(photo);
      setSelectedTripId(photo.tripId || '');
      setApplyTripCoordinates(false); // Reset this when opening the dialog
      setAssignTripDialogOpen(true);

      fetchTrips();
   };

   const handleSaveAssignTrip = async () => {
      if (!photoToAssignTrip) return;

      setLoading(true);
      try {
         // Update the photo with the new tripId (or remove it if empty)
         const photoRef = doc(db, 'photos', photoToAssignTrip.id);
         const updateData: any = {};

         if (selectedTripId) {
            updateData.tripId = selectedTripId;

            // Apply trip coordinates if the option is selected
            if (applyTripCoordinates) {
               const selectedTrip = trips.find((trip) => trip.id === selectedTripId);

               if (selectedTrip && selectedTrip.primaryDestination?.coordinates) {
                  updateData.location = {
                     coordinates: selectedTrip.primaryDestination.coordinates,
                  };
               }
            }
         } else {
            // If clearing the trip assignment, use FieldValue.delete()
            // But for simplicity, we'll just set it to null here
            updateData.tripId = null;
         }

         await updateDoc(photoRef, updateData);

         // Update trip photo count if needed
         if (selectedTripId) {
            const tripRef = doc(db, 'trips', selectedTripId);
            const trip = await getDoc(tripRef);

            if (trip.exists()) {
               const { photoCount = 0 } = trip.data();
               await updateDoc(tripRef, { photoCount: photoCount + 1 });
            }
         }

         // If the photo was previously assigned to a different trip,
         // update that trip's photo count
         if (photoToAssignTrip.tripId && photoToAssignTrip.tripId !== selectedTripId) {
            const oldTripRef = doc(db, 'trips', photoToAssignTrip.tripId);
            const oldTrip = await getDoc(oldTripRef);

            if (oldTrip.exists()) {
               const { photoCount = 0 } = oldTrip.data();
               await updateDoc(oldTripRef, {
                  photoCount: Math.max(0, photoCount - 1),
               });
            }
         }

         // Update local state
         const updatedPhoto = {
            ...photoToAssignTrip,
            tripId: selectedTripId || undefined,
         };

         // Apply trip coordinates in the local state if the option is selected
         if (applyTripCoordinates && selectedTripId) {
            const selectedTrip = trips.find((trip) => trip.id === selectedTripId);

            if (selectedTrip && selectedTrip.primaryDestination?.coordinates) {
               updatedPhoto.location = {
                  ...updatedPhoto.location,
                  coordinates: selectedTrip.primaryDestination.coordinates,
               };
            }
         }

         setPhotos(photos.map((p) => (p.id === photoToAssignTrip.id ? updatedPhoto : p)));
         setFilteredPhotos(
            filteredPhotos.map((p) => (p.id === photoToAssignTrip.id ? updatedPhoto : p))
         );

         // Close dialog
         setAssignTripDialogOpen(false);
         setPhotoToAssignTrip(null);
      } catch (error) {
         console.error('Error assigning trip:', error);
      } finally {
         setLoading(false);
      }
   };

   const handleEditPhotoDetails = (photo: Photo) => {
      setPhotoToEdit(photo);
      setEditDetailsDialogOpen(true);
   };

   const handleSavePhotoDetails = async (
      photoId: string,
      updates: {
         title?: string;
         description?: string;
         location?: {
            name?: string;
            coordinates?: [number, number];
         };
      }
   ) => {
      try {
         const photoRef = doc(db, 'photos', photoId);
         await updateDoc(photoRef, updates);

         // Update photos in local state
         const updatedPhotos = photos.map((photo) => {
            if (photo.id === photoId) {
               return {
                  ...photo,
                  title: updates.title || photo.title,
                  description: updates.description || photo.description,
                  location: updates.location
                     ? {
                          ...photo.location,
                          ...updates.location,
                       }
                     : photo.location,
               };
            }
            return photo;
         });

         setPhotos(updatedPhotos);
         setFilteredPhotos(
            updatedPhotos.filter((photo) => {
               // Apply current filters
               const matchesSearch =
                  !searchTerm ||
                  photo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  photo.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  photo.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

               const matchesLocationFilter =
                  locationFilter === 'all' ||
                  (locationFilter === 'with' && photo.location && photo.location.coordinates) ||
                  (locationFilter === 'without' &&
                     (!photo.location || !photo.location.coordinates));

               const matchesTripFilter =
                  tripFilter === 'all' ||
                  (tripFilter === 'unassigned' && !photo.tripId) ||
                  photo.tripId === tripFilter;

               return matchesSearch && matchesLocationFilter && matchesTripFilter;
            })
         );

         setSnackbar({
            open: true,
            message: 'Photo details updated successfully',
            severity: 'success',
         });
      } catch (error) {
         console.error('Error updating photo details:', error);
         setSnackbar({
            open: true,
            message: 'Failed to update photo details',
            severity: 'error',
         });
         throw error;
      }
   };

   const handleApplyTripCoordinates = async (photo: Photo) => {
      if (!photo.tripId) return;

      setLoading(true);
      try {
         // Find the trip that the photo is assigned to
         const selectedTrip = trips.find((trip) => trip.id === photo.tripId);

         if (!selectedTrip || !selectedTrip.primaryDestination?.coordinates) {
            alert("This trip doesn't have coordinates to apply.");
            return;
         }

         // Update the photo with the trip's coordinates
         const photoRef = doc(db, 'photos', photo.id);
         await updateDoc(photoRef, {
            location: {
               coordinates: selectedTrip.primaryDestination.coordinates,
            },
         });

         // Update local state
         const updatedPhoto = {
            ...photo,
            location: {
               ...photo.location,
               coordinates: selectedTrip.primaryDestination.coordinates,
            },
         };

         setPhotos(photos.map((p) => (p.id === photo.id ? updatedPhoto : p)));
         setFilteredPhotos(filteredPhotos.map((p) => (p.id === photo.id ? updatedPhoto : p)));

         // Show success message
         setSnackbar({
            open: true,
            message: 'Trip coordinates applied to photo',
            severity: 'success',
         });
      } catch (error) {
         console.error('Error applying trip coordinates:', error);
         setSnackbar({
            open: true,
            message: 'Failed to apply trip coordinates',
            severity: 'error',
         });
      } finally {
         setLoading(false);
      }
   };

   return (
      <Container 
         maxWidth='lg'
         sx={{ 
            py: isMobile ? 2 : 3,
            px: isMobile ? 1 : 3
         }}
      >
         <Typography 
            variant='h5' 
            gutterBottom 
            component='div'
            sx={{
               fontSize: isMobile ? fontSize.h4 : fontSize.h3,
               textAlign: isMobile ? 'center' : 'left',
               mb: isMobile ? 2 : 3,
            }}
         >
            Photo Management
         </Typography>

         <Paper sx={{ 
            p: isMobile ? 1.5 : 2, 
            mb: isMobile ? 2 : 3,
            borderRadius: isMobile ? 1 : 2
         }}>
            <Box sx={{ 
               display: 'flex', 
               gap: isMobile ? 1 : 2, 
               alignItems: isMobile ? 'stretch' : 'center', 
               mb: isMobile ? 1.5 : 2, 
               flexWrap: 'wrap',
               flexDirection: isMobile ? 'column' : 'row'
            }}>
               <TextField
                  label='Search Photos'
                  variant='outlined'
                  size='small'
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{ flexGrow: 1, minWidth: '200px' }}
                  InputProps={{
                     startAdornment: (
                        <InputAdornment position='start'>
                           <SearchIcon />
                        </InputAdornment>
                     ),
                  }}
               />

               <Button
                  variant={locationFilter !== 'all' ? 'contained' : 'outlined'}
                  color={
                     locationFilter === 'with'
                        ? 'success'
                        : locationFilter === 'without'
                          ? 'error'
                          : 'primary'
                  }
                  onClick={toggleLocationFilter}
                  startIcon={
                     locationFilter === 'with' ? (
                        <LocationIcon />
                     ) : locationFilter === 'without' ? (
                        <LocationOffIcon />
                     ) : (
                        <LocationIcon />
                     )
                  }
                  size='small'
               >
                  {locationFilter === 'all'
                     ? 'All Locations'
                     : locationFilter === 'with'
                       ? 'With Location'
                       : 'Without Location'}
               </Button>

               <FormControl sx={{ minWidth: 180 }} size='small'>
                  <InputLabel id='trip-filter-label'>Trip Filter</InputLabel>
                  <Select
                     labelId='trip-filter-label'
                     id='trip-filter'
                     value={tripFilter}
                     label='Trip Filter'
                     onChange={handleTripFilterChange}
                     startAdornment={
                        <InputAdornment position='start'>
                           <FilterIcon fontSize='small' />
                        </InputAdornment>
                     }
                  >
                     <MenuItem value='all'>All Photos</MenuItem>
                     <MenuItem value='unassigned'>Unassigned Photos</MenuItem>
                     <Divider />
                     {trips.map((trip) => (
                        <MenuItem key={trip.id} value={trip.id}>
                           {trip.title}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
            </Box>

            <Typography variant='body2' color='text.secondary'>
               Showing {filteredPhotos.length} of {photos.length} photos
               {locationFilter !== 'all' &&
                  (locationFilter === 'with'
                     ? ` (with location data)`
                     : ` (without location data)`)}
               {tripFilter !== 'all' &&
                  (tripFilter === 'unassigned'
                     ? ` (unassigned to trips)`
                     : ` (in trip: ${trips.find((t) => t.id === tripFilter)?.title || tripFilter})`)}
            </Typography>
         </Paper>

         {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
               <CircularProgress />
            </Box>
         ) : isMobile ? (
            // Mobile Card Layout
            <Box>
               <Grid container spacing={2}>
                  {filteredPhotos
                     .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                     .map((photo) => (
                        <Grid item xs={12} sm={6} md={4} key={photo.id}>
                           <Card sx={{ 
                              maxHeight: 400,
                              display: 'flex',
                              flexDirection: 'column',
                              borderRadius: isMobile ? 1 : 2,
                           }}>
                              <CardMedia
                                 component='img'
                                 height='200'
                                 image={photo.thumbnailUrl || photo.url}
                                 alt={photo.title}
                                 sx={{ objectFit: 'cover' }}
                              />
                              <CardContent sx={{ flexGrow: 1, p: isMobile ? 1.5 : 2 }}>
                                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                    <Typography 
                                       variant='h6' 
                                       component='div' 
                                       noWrap
                                       sx={{
                                          fontSize: isMobile ? fontSize.body1 : fontSize.h6,
                                          fontWeight: 600,
                                          flex: 1,
                                          mr: 1
                                       }}
                                    >
                                       {photo.title}
                                    </Typography>
                                    {currentUser && (
                                       <PhotoLikeButton
                                          photoId={photo.id}
                                          userId={currentUser.uid}
                                          initialLikeCount={photo.likeCount || 0}
                                          initialIsLiked={photo.isLiked || false}
                                          size="small"
                                          variant="compact"
                                          showCount={true}
                                       />
                                    )}
                                 </Box>
                                 {photo.description && (
                                    <Typography 
                                       variant='body2' 
                                       color='text.secondary'
                                       sx={{
                                          fontSize: isMobile ? fontSize.body2 : fontSize.body1,
                                          mb: 1,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                       }}
                                    >
                                       {photo.description}
                                    </Typography>
                                 )}
                                 
                                 {/* Date */}
                                 <Typography 
                                    variant='caption' 
                                    color='text.secondary'
                                    sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                                 >
                                    {photo.takenAt.toLocaleDateString()} {photo.takenAt.toLocaleTimeString()}
                                 </Typography>
                                 
                                 {/* Location */}
                                 <Box sx={{ mt: 1, mb: 1 }}>
                                    {photo.location && photo.location.coordinates && photo.location.coordinates.length === 2 ? (
                                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                          <LocationIcon color='success' fontSize='small' sx={{ mr: 0.5 }} />
                                          <Typography variant='caption' sx={{ fontSize: '0.75rem' }}>
                                             {photo.location.coordinates[1]?.toFixed(4)}, {photo.location.coordinates[0]?.toFixed(4)}
                                          </Typography>
            </Box>
         ) : (
                                       <Chip
                                          icon={<LocationOffIcon />}
                                          label='No location'
                                          size='small'
                                          color='error'
                                          variant='outlined'
                                          sx={{ fontSize: '0.75rem' }}
                                       />
                                    )}
                                 </Box>
                                 
                                 {/* Trip */}
                                 <Box sx={{ mb: 1 }}>
                                    {photo.tripId ? (
                                       <Chip
                                          label={trips.find((t) => t.id === photo.tripId)?.title || 'Unknown Trip'}
                                          size='small'
                                          color='primary'
                                          sx={{ fontSize: '0.75rem' }}
                                       />
                                    ) : (
                                       <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
                                          No trip assigned
                                       </Typography>
                                    )}
                                 </Box>
                                 
                                 {/* Tags */}
                                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                    {photo.tags && photo.tags.length > 0 ? (
                                       photo.tags.slice(0, 3).map((tag) => (
                                          <Chip
                                             key={tag}
                                             label={tag}
                                             size='small'
                                             icon={<TagIcon fontSize='small' />}
                                             sx={{ fontSize: '0.7rem' }}
                                          />
                                       ))
                                    ) : (
                                       <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.75rem' }}>
                                          No tags
                                       </Typography>
                                    )}
                                 </Box>
                              </CardContent>
                              <CardActions sx={{ p: isMobile ? 1 : 1.5, pt: 0 }}>
                                 <Button
                                    size='small'
                                    color='primary'
                                    onClick={() => handleEditPhotoDetails(photo)}
                                    sx={{ fontSize: '0.75rem' }}
                                 >
                                    Edit
                                 </Button>
                                 <Button
                                    size='small'
                                    color='error'
                                    onClick={() => handleDeleteClick(photo)}
                                    sx={{ fontSize: '0.75rem' }}
                                 >
                                    Delete
                                 </Button>
                              </CardActions>
                           </Card>
                        </Grid>
                     ))}
               </Grid>
               <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TablePagination
                     component='div'
                     count={filteredPhotos.length}
                     page={page}
                     onPageChange={handleChangePage}
                     rowsPerPage={rowsPerPage}
                     onRowsPerPageChange={handleChangeRowsPerPage}
                     rowsPerPageOptions={[5, 10, 25]}
                  />
               </Box>
            </Box>
         ) : (
            // Desktop Table Layout
            <Paper>
               <TableContainer>
                  <Table sx={{ minWidth: 650 }} aria-label='photos table'>
                     <TableHead>
                        <TableRow>
                           <TableCell>Thumbnail</TableCell>
                           <TableCell>Title</TableCell>
                           <TableCell>Date Taken</TableCell>
                           <TableCell>Location</TableCell>
                           <TableCell>Trip</TableCell>
                           <TableCell>Tags</TableCell>
                           <TableCell>Actions</TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {filteredPhotos
                           .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                           .map((photo) => (
                              <TableRow key={photo.id} hover>
                                 <TableCell>
                                    <Box
                                       component='img'
                                       src={photo.thumbnailUrl || photo.url}
                                       alt={photo.title}
                                       sx={{
                                          width: 80,
                                          height: 60,
                                          objectFit: 'cover',
                                          borderRadius: 1,
                                       }}
                                    />
                                 </TableCell>
                                 <TableCell>
                                    <FixedListItemText
                                       primary={photo.title}
                                       secondary={photo.description || ''}
                                    />
                                 </TableCell>
                                 <TableCell>
                                    {photo.takenAt.toLocaleDateString()}
                                    <br />
                                    <Typography variant='caption' color='text.secondary'>
                                       {photo.takenAt.toLocaleTimeString()}
                                    </Typography>
                                 </TableCell>
                                 <TableCell>
                                    {photo.location &&
                                    photo.location.coordinates &&
                                    photo.location.coordinates.length === 2 ? (
                                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <LocationIcon
                                             color='success'
                                             fontSize='small'
                                             sx={{ mr: 0.5 }}
                                          />
                                          <Typography variant='body2'>
                                             {photo.location.coordinates[1] !== undefined
                                                ? photo.location.coordinates[1].toFixed(6)
                                                : 'N/A'}
                                             ,
                                             <br />
                                             {photo.location.coordinates[0] !== undefined
                                                ? photo.location.coordinates[0].toFixed(6)
                                                : 'N/A'}
                                          </Typography>
                                          <IconButton
                                             size='small'
                                             onClick={() => handleEditLocation(photo)}
                                             title='Edit Location'
                                          >
                                             <EditIcon fontSize='small' />
                                          </IconButton>
                                       </Box>
                                    ) : (
                                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Chip
                                             icon={<LocationOffIcon />}
                                             label='No location data'
                                             size='small'
                                             color='error'
                                             variant='outlined'
                                             sx={{ mr: 1 }}
                                          />
                                          <Button
                                             size='small'
                                             variant='outlined'
                                             startIcon={<LocationIcon />}
                                             onClick={() => handleEditLocation(photo)}
                                          >
                                             Add
                                          </Button>

                                          {photo.tripId && (
                                             <Button
                                                size='small'
                                                variant='outlined'
                                                sx={{ ml: 1 }}
                                                onClick={() => handleApplyTripCoordinates(photo)}
                                                title='Use Trip Coordinates'
                                             >
                                                Use Trip Coords
                                             </Button>
                                          )}
                                       </Box>
                                    )}
                                 </TableCell>
                                 <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                       {photo.tripId ? (
                                          <>
                                             <Chip
                                                label={
                                                   trips.find((t) => t.id === photo.tripId)
                                                      ?.title || 'Unknown Trip'
                                                }
                                                size='small'
                                                color='primary'
                                                sx={{ mr: 1 }}
                                             />
                                             <IconButton
                                                size='small'
                                                onClick={() => handleAssignTrip(photo)}
                                                title='Change Trip'
                                             >
                                                <EditIcon fontSize='small' />
                                             </IconButton>
                                          </>
                                       ) : (
                                          <Button
                                             size='small'
                                             variant='outlined'
                                             onClick={() => handleAssignTrip(photo)}
                                             startIcon={<ImageIcon />}
                                          >
                                             Assign Trip
                                          </Button>
                                       )}
                                    </Box>
                                 </TableCell>
                                 <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                       {photo.tags && photo.tags.length > 0 ? (
                                          photo.tags.map((tag) => (
                                             <Chip
                                                key={tag}
                                                label={tag}
                                                size='small'
                                                icon={<TagIcon fontSize='small' />}
                                             />
                                          ))
                                       ) : (
                                          <Typography variant='body2' color='text.secondary'>
                                             No tags
                                          </Typography>
                                       )}
                                    </Box>
                                 </TableCell>
                                 <TableCell>
                                    <IconButton
                                       size='small'
                                       color='primary'
                                       onClick={() => handleEditPhotoDetails(photo)}
                                       title='Edit Photo Details'
                                    >
                                       <EditIcon fontSize='small' />
                                    </IconButton>
                                    <IconButton
                                       size='small'
                                       color='error'
                                       onClick={() => handleDeleteClick(photo)}
                                    >
                                       <DeleteIcon fontSize='small' />
                                    </IconButton>
                                 </TableCell>
                              </TableRow>
                           ))}
                        {filteredPhotos.length === 0 && (
                           <TableRow>
                              <TableCell colSpan={7} align='center' sx={{ py: 3 }}>
                                 <Typography variant='body1' color='text.secondary'>
                                    No photos found
                                 </Typography>
                              </TableCell>
                           </TableRow>
                        )}
                     </TableBody>
                  </Table>
               </TableContainer>
               <TablePagination
                  component='div'
                  count={filteredPhotos.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage='Photos per page:'
               />
            </Paper>
         )}

         {/* Delete confirmation dialog */}
         <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Are you sure you want to delete {photoToDelete?.title || photoToDelete?.filename}?
                  This action cannot be undone.
               </DialogContentText>
               {photoToDelete && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                     <img
                        src={photoToDelete.thumbnailUrl || photoToDelete.url}
                        alt={photoToDelete.title}
                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                     />
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel}>Cancel</Button>
               <Button onClick={handleDeleteConfirm} color='error' variant='contained'>
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {/* Location edit dialog */}
         <Dialog
            open={locationDialogOpen}
            onClose={handleLocationCancel}
            maxWidth='md'
            fullWidth
            aria-labelledby='location-dialog-title'
         >
            <DialogTitle id='location-dialog-title'>
               {photoToEditLocation?.location?.coordinates
                  ? 'Edit Photo Location'
                  : 'Add Photo Location'}
            </DialogTitle>
            <DialogContent>
               {photoToEditLocation && (
                  <>
                     <Box sx={{ display: 'flex', mb: 2 }}>
                        <img
                           src={photoToEditLocation.thumbnailUrl || photoToEditLocation.url}
                           alt={photoToEditLocation.title}
                           style={{
                              width: 120,
                              height: 90,
                              objectFit: 'cover',
                              borderRadius: 4,
                              marginRight: 16,
                           }}
                        />
                        <Box>
                           <Typography variant='subtitle1'>
                              {photoToEditLocation.title || photoToEditLocation.filename}
                           </Typography>
                           <Typography variant='body2' color='text.secondary'>
                              {photoToEditLocation.takenAt.toLocaleDateString()}
                           </Typography>
                        </Box>
                     </Box>

                     <Typography variant='body2' gutterBottom>
                        Click on the map to set a location, or drag the marker to adjust it.
                     </Typography>

                     <Box
                        ref={locationMapContainer}
                        sx={{
                           height: 400,
                           width: '100%',
                           mb: 2,
                           border: '1px solid #ddd',
                           borderRadius: 1,
                           position: 'relative', // Important for mapbox
                        }}
                     />

                     {newCoordinates && (
                        <Typography variant='body2'>
                           Selected coordinates: {newCoordinates[1].toFixed(6)},{' '}
                           {newCoordinates[0].toFixed(6)}
                        </Typography>
                     )}
                  </>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleLocationCancel}>Cancel</Button>
               <Button
                  onClick={handleLocationSave}
                  color='primary'
                  variant='contained'
                  disabled={!newCoordinates}
               >
                  Save Location
               </Button>
            </DialogActions>
         </Dialog>

         {/* Trip assignment dialog */}
         <Dialog
            open={assignTripDialogOpen}
            onClose={() => setAssignTripDialogOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               {photoToAssignTrip?.tripId ? 'Change Trip Assignment' : 'Assign to Trip'}
            </DialogTitle>
            <DialogContent>
               {photoToAssignTrip && (
                  <>
                     <Box sx={{ display: 'flex', mb: 3 }}>
                        <img
                           src={photoToAssignTrip.thumbnailUrl || photoToAssignTrip.url}
                           alt={photoToAssignTrip.title}
                           style={{
                              width: 120,
                              height: 90,
                              objectFit: 'cover',
                              borderRadius: 4,
                              marginRight: 16,
                           }}
                        />
                        <Box>
                           <Typography variant='subtitle1'>
                              {photoToAssignTrip.title || photoToAssignTrip.filename}
                           </Typography>
                           <Typography variant='body2' color='text.secondary'>
                              {photoToAssignTrip.takenAt.toLocaleDateString()}
                           </Typography>
                        </Box>
                     </Box>

                     <FormControl fullWidth>
                        <InputLabel id='trip-select-label'>Trip</InputLabel>
                        <Select
                           labelId='trip-select-label'
                           value={selectedTripId}
                           label='Trip'
                           onChange={(e) => setSelectedTripId(e.target.value)}
                        >
                           <MenuItem value=''>
                              <em>None (Unassigned)</em>
                           </MenuItem>
                           {trips.map((trip) => (
                              <MenuItem key={trip.id} value={trip.id}>
                                 {trip.title} ({format(trip.startDate, 'MMM d, yyyy')})
                              </MenuItem>
                           ))}
                        </Select>
                     </FormControl>

                     {selectedTripId && (
                        <FormControlLabel
                           control={
                              <Checkbox
                                 checked={applyTripCoordinates}
                                 onChange={(e) => setApplyTripCoordinates(e.target.checked)}
                              />
                           }
                           label='Apply trip coordinates to photo'
                           sx={{ mt: 2 }}
                        />
                     )}
                  </>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setAssignTripDialogOpen(false)}>Cancel</Button>
               <Button onClick={handleSaveAssignTrip} color='primary' variant='contained'>
                  Save
               </Button>
            </DialogActions>
         </Dialog>

         {/* Edit Photo Details Dialog */}
         <EditPhotoDetailsDialog
            open={editDetailsDialogOpen}
            photo={photoToEdit}
            onClose={() => {
               setEditDetailsDialogOpen(false);
               setPhotoToEdit(null);
            }}
            onSave={handleSavePhotoDetails}
         />

         {/* Add Snackbar component at the end */}
         <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={handleCloseSnackbar}
               severity={snackbar.severity}
               sx={{ width: '100%' }}
            >
               {snackbar.message}
            </Alert>
         </Snackbar>
      </Container>
   );
};

export default PhotoManagement;
